import cloudinaryService from './CloudinaryService';

class GameStateManager {
  constructor() {
    this.version = '1.0';
  }

  /**
   * איסוף כל נתוני המשחק מהקונטקסטים השונים
   * @param {Object} contexts - כל הקונטקסטים
   * @returns {Object} מצב המשחק המלא
   */
  async collectGameState(contexts) {
    const {
      layerContext,
      containerContext,
      gameContext,
      historyContext,
      authContext,
      tokenContext
    } = contexts;

    try {
      // איסוף נתוני שכבות
      const layersData = await this.serializeLayers(layerContext);
      
      // איסוף נתוני קבוצות שכבות
      const layerGroups = this.serializeLayerGroups(layerContext);
      
      // איסוף נתוני קונטיינרים
      const containers = await this.serializeContainers(containerContext);
      
      // איסוף נתוני טוקנים
      const tokensData = await this.serializeTokens(tokenContext);
      
      // איסוף נתוני קבוצות טוקנים
      const tokenGroups = this.serializeTokenGroups(tokenContext);
      
      // איסוף נתוני משחק כלליים
      const gameSettings = this.serializeGameSettings(gameContext);
      
      // איסוף מצב היסטוריה (מקוצר)
      const historyState = this.serializeHistory(historyContext);
      
      // יצירת metadata
      const metadata = {
        id: `game_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        version: this.version,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        author: authContext?.currentUser?.userId || 'anonymous',
        authorName: authContext?.currentUser?.username || 'Anonymous User'
      };

      const gameState = {
        metadata,
        layers: layersData.layers,
        activeLayerId: layersData.activeLayerId,
        layerGroups,
        containers,
        tokens: tokensData.tokens,
        selectedTokenIds: tokensData.selectedTokenIds,
        tokenGroups,
        gameSettings,
        historyState
      };

      // חישוב גודל משוער
      const estimatedSize = this.estimateSize(gameState);
      gameState.metadata.estimatedSize = estimatedSize;

      return gameState;

    } catch (error) {
      console.error('[GameStateManager] Error collecting game state:', error);
      throw new Error(`Failed to collect game state: ${error.message}`);
    }
  }

  /**
   * עיבוד תמונות לשמירה - מעלה רק תמונות חדשות שעוד לא קיימות ב-Cloudinary
   */
  async processImagesForSave(gameState, gameId) {
    const processedState = JSON.parse(JSON.stringify(gameState));
    

    
    // עיבוד תמונות בשכבות
    if (processedState.layers) {
      for (const layer of processedState.layers) {
        // Parse layer_data if it's a string
        let layerData = layer.layer_data;
        if (typeof layerData === 'string') {
          try {
            layerData = JSON.parse(layerData);
          } catch (e) {
            continue;
          }
        }
        
        // בדיקה אם יש תמונה שצריכה העלאה
        if (layerData && layerData.image && layerData.image.src) {
          // 🔧 תיקון: בדיקה מתקדמת - העלה רק אם התמונה לא קיימת ב-Cloudinary
          const needsUpload = this.shouldUploadImage(layerData.image);
          
          if (needsUpload) {
            console.log(`[GameStateManager] Uploading new layer image for layer ${layer.layer_id}`);
            
          const uploadResult = await cloudinaryService.uploadImage(
            layerData.image.src,
            gameId,
            'layer',
            layer.layer_id
          );
          
          if (uploadResult.success) {
            layerData.image.src = uploadResult.optimizedUrl || uploadResult.url;
            layerData.image.cloudinaryPublicId = uploadResult.publicId;
            layerData.image.cloudinaryId = uploadResult.cloudinaryId;
            layerData.image.folder = uploadResult.folder;
            layerData.image.tags = uploadResult.tags;
            
            // Update layer_data back to string
            layer.layer_data = JSON.stringify(layerData);
            }
          } else {
            console.log(`[GameStateManager] Skipping upload for existing layer image: ${layerData.image.cloudinaryPublicId || layerData.image.src}`);
          }
        }
      }
    }

    // עיבוד תמונות בקונטיינרים
    if (processedState.containers) {
      for (const container of processedState.containers) {
        if (container.image && container.image.src) {
          // 🔧 תיקון: בדיקה מתקדמת - העלה רק אם התמונה לא קיימת ב-Cloudinary
          const needsUpload = this.shouldUploadImage(container.image);
          
          if (needsUpload) {
            console.log(`[GameStateManager] Uploading new container image for container ${container.id}`);
            
          const uploadResult = await cloudinaryService.uploadImage(
            container.image.src,
            gameId,
            'container',
            container.id
          );
          
          if (uploadResult.success) {
            container.image.src = uploadResult.optimizedUrl || uploadResult.url;
            container.image.cloudinaryPublicId = uploadResult.publicId;
            container.image.cloudinaryId = uploadResult.cloudinaryId;
            container.image.folder = uploadResult.folder;
            container.image.tags = uploadResult.tags;
          } else {
            console.error('[GameStateManager] Failed to upload image for container:', container.id, uploadResult.error);
            }
          }
        }
      }
    }

    // עיבוד תמונות בטוקנים
    if (processedState.tokens) {
      for (const token of processedState.tokens) {
        if (token.image && token.image.src) {
          // 🔧 תיקון: בדיקה מתקדמת - העלה רק אם התמונה לא קיימת ב-Cloudinary
          const needsUpload = this.shouldUploadImage(token.image);
          
          if (needsUpload) {
            console.log(`[GameStateManager] Uploading new token image for token ${token.id}`);
            
          const uploadResult = await cloudinaryService.uploadImage(
            token.image.src,
            gameId,
            'token',
            token.id
          );
          
          if (uploadResult.success) {
            token.image.src = uploadResult.optimizedUrl || uploadResult.url;
            token.image.cloudinaryPublicId = uploadResult.publicId;
            token.image.cloudinaryId = uploadResult.cloudinaryId;
            token.image.folder = uploadResult.folder;
            token.image.tags = uploadResult.tags;
          } else {
            console.error('[GameStateManager] Failed to upload image for token:', token.id, uploadResult.error);
            }
          }
        }
      }
    }

    return processedState;
  }

  /**
   * 🔧 תיקון: קובע אם תמונה צריכה להיות מועלית ל-Cloudinary
   * @param {Object} imageObject - אובייקט התמונה
   * @returns {boolean} האם צריך להעלות את התמונה
   */
  shouldUploadImage(imageObject) {
    if (!imageObject || !imageObject.src) {
      return false; // אין תמונה
    }
    
    // אם יש cloudinaryPublicId או cloudinaryId, התמונה כבר קיימת ב-Cloudinary
    if (imageObject.cloudinaryPublicId || imageObject.cloudinaryId) {
      return false; // התמונה כבר קיימת
    }
    
    // אם זה URL של Cloudinary, התמונה כבר קיימת
    if (cloudinaryService.isCloudinaryUrl(imageObject.src)) {
      return false; // התמונה כבר ב-Cloudinary
    }
    
    // בדיקה מתקדמת: אם ה-src כולל transformations של Cloudinary אבל לא נתפס בבדיקה הקודמת
    if (imageObject.src.includes('res.cloudinary.com') || imageObject.src.includes('cloudinary.com')) {
      return false;
    }
    
    // אם זה data URL או blob URL, צריך להעלות
    if (imageObject.src.startsWith('data:') || imageObject.src.startsWith('blob:')) {
      return true; // תמונה חדשה שצריכה העלאה
    }
    
    // אם זה URL רגיל אבל לא Cloudinary, ככל הנראה תמונה חדשה
    return true;
  }

  /**
   * שמירת מצב המשחק
   * @param {Object} gameState - מצב המשחק
   * @param {Object} saveOptions - אפשרויות שמירה
   * @returns {Promise<Object>} תוצאת השמירה
   */
  async saveGameState(gameState, saveOptions = {}) {
    const {
      name = 'Untitled Game',
      description = '',
      isPublic = false,
      gameId
    } = saveOptions;

    try {
      // עדכון metadata
      gameState.metadata.name = name;
      gameState.metadata.description = description;
      gameState.metadata.isPublic = isPublic;
      gameState.metadata.updatedAt = new Date().toISOString();

      let finalGameId = gameId;
      
      // אם אין gameId (משחק חדש), צור שמירה ראשונית במסד הנתונים כדי לקבל gameId
      if (!finalGameId) {
        const initialSave = await this.saveToDatabase({
          name,
          description,
          cloudinaryUrl: null, // ימולא מאוחר יותר
          cloudinaryPublicId: null,
          cloudinaryId: null,
          thumbnailUrl: null,
          isPublic,
          metadata: {
            version: gameState.metadata.version,
            estimatedSize: 0,
            layerCount: gameState.layers?.length || 0,
            containerCount: gameState.containers?.length || 0,
            tokenCount: gameState.tokens?.length || 0,
            folder: null,
            tags: []
          }
        });
        finalGameId = initialSave.id;
      }

      // העלאת תמונות ל-Cloudinary עם gameId
      const processedGameState = await this.processImagesForSave(gameState, finalGameId);

      // יצירת thumbnail למשחק
      const thumbnail = await this.generateGameThumbnail(processedGameState, finalGameId);

      // העלאת מצב המשחק ל-Cloudinary עם ארגון באלבום
      const uploadResult = await cloudinaryService.uploadGameState(
        processedGameState,
        finalGameId,
        name
      );

      if (!uploadResult.success) {
        console.error('[GameStateManager] Cloudinary upload failed:', uploadResult.error);
        throw new Error(uploadResult.error);
      }

      // עדכון במסד הנתונים עם פרטי Cloudinary
      await this.updateInDatabase(finalGameId, {
        name,
        description,
        cloudinaryUrl: uploadResult.url,
        cloudinaryPublicId: uploadResult.publicId,
        cloudinaryId: uploadResult.cloudinaryId, // המזהה הייעודי
        thumbnailUrl: thumbnail?.url,
        isPublic,
        metadata: {
          version: gameState.metadata.version,
          estimatedSize: uploadResult.bytes,
          layerCount: gameState.layers?.length || 0,
          containerCount: gameState.containers?.length || 0,
          tokenCount: gameState.tokens?.length || 0,
          folder: uploadResult.folder,
          tags: uploadResult.tags
        }
      });

      return {
        success: true,
        gameStateId: finalGameId,
        cloudinaryUrl: uploadResult.url,
        cloudinaryId: uploadResult.cloudinaryId,
        thumbnailUrl: thumbnail?.url,
        size: uploadResult.bytes
      };

    } catch (error) {
      console.error('[GameStateManager] Error saving game state:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * עדכון מצב משחק קיים (Quick Save או Save As)
   * @param {string} gameStateId - מזהה מצב המשחק
   * @param {Object} gameState - מצב המשחק החדש
   * @param {Object} saveOptions - אפשרויות שמירה
   * @returns {Promise<Object>} תוצאת העדכון
   */
  async updateGameState(gameStateId, gameState, saveOptions = {}) {
    try {
      const { name, description, isPublic, generateThumbnail = false } = saveOptions;

      console.log(`[GameStateManager] Updating game ${gameStateId}, generateThumbnail: ${generateThumbnail}`);

      // עיבוד תמונות - משתמש ב-gameStateId כי זה משחק קיים
      const processedGameState = await this.processImagesForSave(gameState, gameStateId);

      // 🔧 תיקון: יצירת thumbnail רק אם נדרש (לא ב-Quick Save)
      let thumbnail = null;
      let thumbnailUrl = null;
      
      if (generateThumbnail) {
        console.log(`[GameStateManager] Generating new thumbnail for game ${gameStateId}`);
        thumbnail = await this.generateGameThumbnail(processedGameState, gameStateId);
        thumbnailUrl = thumbnail?.url;
      } else {
        console.log(`[GameStateManager] Skipping thumbnail generation for Quick Save`);
        // שמור על הthumbnail הקיים - קבל אותו מבסיס הנתונים
        const existingGame = await this.getGameFromDatabase(gameStateId);
        if (existingGame.success && existingGame.data && existingGame.data.thumbnail_url) {
          thumbnailUrl = existingGame.data.thumbnail_url;
          console.log(`[GameStateManager] Using existing thumbnail: ${thumbnailUrl}`);
        }
      }

      // קבלת הנתונים הקיימים כדי לקבל את ה-cloudinary_public_id הנכון
      const existingGame = await this.getGameFromDatabase(gameStateId);
      let existingCloudinaryId = null;
      
      if (existingGame.success && existingGame.data) {
        // שימוש ב-cloudinary_public_id מבסיס הנתונים במקום metadata.cloudinaryId
        existingCloudinaryId = existingGame.data.cloudinary_public_id;
        
        // אם אין cloudinary_public_id, נסה לקחת מה-metadata (לתאימות לאחור)
        if (!existingCloudinaryId && existingGame.data.metadata) {
          try {
            const metadata = typeof existingGame.data.metadata === 'string' 
              ? JSON.parse(existingGame.data.metadata) 
              : existingGame.data.metadata;
            existingCloudinaryId = metadata.cloudinaryId;
          } catch (e) {
            console.warn('Could not parse existing metadata for cloudinaryId');
          }
        }
      }

      // העלאה ל-Cloudinary עם שימוש ב-gameStateId
      const uploadResult = await cloudinaryService.uploadGameState(
        processedGameState,
        gameStateId,
        name,
        existingCloudinaryId
      );

      if (!uploadResult.success) {
        throw new Error(uploadResult.error);
      }

      // עדכון בבסיס הנתונים עם תוכן המשחק לסינכרון תמונות
      const updateResult = await this.updateInDatabase(gameStateId, {
        name,
        description,
        cloudinaryUrl: uploadResult.url,
        cloudinaryPublicId: uploadResult.publicId,
        cloudinaryId: uploadResult.cloudinaryId,
        thumbnailUrl: thumbnailUrl, // 🔧 תיקון: השתמש בthumbnailUrl שחושב
        isPublic,
        gameContent: processedGameState, // 🆕 הוספת תוכן המשחק לסינכרון
        metadata: {
          version: gameState.metadata?.version || '1.0',
          estimatedSize: uploadResult.bytes,
          layerCount: gameState.layers?.length || 0,
          containerCount: gameState.containers?.length || 0,
          tokenCount: gameState.tokens?.length || 0,
          folder: uploadResult.folder,
          tags: uploadResult.tags
        }
      });

      return {
        success: true,
        gameStateId: updateResult.id,
        cloudinaryUrl: uploadResult.url,
        cloudinaryId: uploadResult.cloudinaryId,
        thumbnailUrl: thumbnailUrl, // 🔧 תיקון: השתמש בthumbnailUrl שחושב
        size: uploadResult.bytes
      };

    } catch (error) {
      console.error('Error updating game state:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * טעינת מצב משחק מבסיס הנתונים ו-Cloudinary
   * @param {string} gameStateId - מזהה מצב המשחק
   * @returns {Promise<Object>} תוצאת הטעינה
   */
  async loadGameState(gameStateId) {
    try {
      // קבלת פרטי המשחק מבסיס הנתונים
      const gameInfo = await this.getGameFromDatabase(gameStateId);
      
      if (!gameInfo.success) {
        console.error('[GameStateManager] Failed to get game info from database:', gameInfo.error);
        throw new Error(gameInfo.error);
      }

      // בדיקה שיש cloudinary_url
      if (!gameInfo.data.cloudinary_url) {
        console.error('[GameStateManager] No cloudinary_url found in game data:', gameInfo.data);
        throw new Error('Game state URL not found in database');
      }

      // הורדת מצב המשחק מ-Cloudinary
      const downloadResult = await cloudinaryService.downloadGameState(
        gameInfo.data.cloudinary_url
      );

      if (!downloadResult.success) {
        console.error('[GameStateManager] Failed to download from Cloudinary:', downloadResult.error);
        throw new Error(downloadResult.error);
      }

      const gameState = downloadResult.data;

      // עיבוד תמונות (המרת URLs אם נדרש)
      const processedGameState = await this.processImagesForLoad(gameState);

      return {
        success: true,
        gameState: processedGameState,
        metadata: gameInfo.data
      };

    } catch (error) {
      console.error('[GameStateManager] Error loading game state:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * שחזור מצב המשחק לקונטקסטים
   * @param {Object} gameState - מצב המשחק
   * @param {Object} contexts - הקונטקסטים
   * @returns {Promise<boolean>} האם השחזור הצליח
   */
  async restoreGameState(gameState, contexts) {
    const {
      layerContext,
      containerContext,
      gameContext,
      historyContext,
      tokenContext
    } = contexts;

    try {
      // ניקוי מצב נוכחי
      await this.clearCurrentState(contexts);

      // שחזור שכבות קודם (חשוב שהשכבות יהיו לפני הקונטיינרים)
      let layerIdMapping = {};
      if (gameState.layers && gameState.layers.length > 0) {
        layerIdMapping = await this.deserializeLayers(gameState.layers, layerContext, gameState.activeLayerId);
        
        // המתנה קצרה כדי לוודא שהשכבות נוצרו
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // שחזור קבוצות שכבות אחרי השכבות
      if (gameState.layerGroups && gameState.layerGroups.length > 0) {
        await this.deserializeLayerGroups(gameState.layerGroups, layerContext, layerIdMapping);
        
        // המתנה קצרה כדי לוודא שהקבוצות נוצרו
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      // שחזור קונטיינרים אחרי השכבות
      if (gameState.containers && gameState.containers.length > 0) {
        await this.deserializeContainers(gameState.containers, containerContext, layerIdMapping);
      }

      // שחזור טוקנים
      if ((gameState.tokens && gameState.tokens.length > 0) || gameState.selectedTokenId || gameState.selectedTokenIds) {
        if (tokenContext) {
          // Create the token data structure for deserialization
          const tokenData = {
            tokens: gameState.tokens || [],
            selectedTokenId: gameState.selectedTokenId || null,
            selectedTokenIds: gameState.selectedTokenIds || [],
            tokenGroups: gameState.tokenGroups || []
          };
          
          await this.deserializeTokens(tokenData, tokenContext);
        }
      }

      // שחזור הגדרות משחק
      if (gameState.gameSettings) {
        await this.deserializeGameSettings(gameState.gameSettings, gameContext);
      }

      // שחזור היסטוריה (אופציונלי)
      if (gameState.historyState && historyContext) {
        await this.deserializeHistory(gameState.historyState, historyContext);
      }

      return true;

    } catch (error) {
      console.error('[GameStateManager] Error restoring game state:', error);
      throw new Error(`Failed to restore game state: ${error.message}`);
    }
  }

  /**
   * סריאליזציה של שכבות
   */
  async serializeLayers(layerContext) {
    if (!layerContext || !layerContext.layers) {
      return {
        layers: [],
        activeLayerId: null
      };
    }

    const serializedLayers = layerContext.layers.map(layer => {
      // וודא שיש layer_data תקין
      let layerData = layer.layer_data;
      
      // אם אין layer_data או שהוא לא תקין, ניצור אחד חדש
      if (!layerData) {
        layerData = JSON.stringify({ lines: [] });
      } else if (typeof layerData === 'string') {
        try {
          // בדוק שה-JSON תקין
          JSON.parse(layerData);
        } catch (e) {
          // אם ה-JSON לא תקין, ניצור חדש
          layerData = JSON.stringify({ lines: [] });
        }
      } else if (typeof layerData === 'object') {
        // אם זה כבר אובייקט, נמיר אותו ל-JSON
        layerData = JSON.stringify(layerData);
      }

      return {
        layer_id: layer.layer_id,
        layer_name: layer.layer_name,
        is_visible: layer.is_visible,
        is_wall_layer: layer.is_wall_layer,
        layer_order: layer.layer_order,
        layer_data: layerData,
        createdAt: layer.createdAt,
        updatedAt: layer.updatedAt
      };
    });

    return {
      layers: serializedLayers,
      activeLayerId: layerContext.activeLayerId || null
    };
  }

  /**
   * סריאליזציה של קבוצות שכבות
   */
  serializeLayerGroups(layerContext) {
    if (!layerContext || !layerContext.layerGroups) {
      return [];
    }

    const serializedLayerGroups = layerContext.layerGroups.map(group => {
      return {
        id: group.id,
        name: group.name,
        color: group.color,
        layerIds: [...group.layerIds], // Create a copy of the array
        createdAt: group.createdAt || new Date().toISOString(),
        updatedAt: group.updatedAt || new Date().toISOString()
      };
    });

    return serializedLayerGroups;
  }

  /**
   * סריאליזציה של קונטיינרים
   */
  async serializeContainers(containerContext) {
    if (!containerContext || !containerContext.containers) {
      return [];
    }

    const serializedContainers = containerContext.containers.map(container => {
      return {
        id: container.id,
        name: container.name,
        x: container.x,
        y: container.y,
        width: container.image?.width || container.width,
        height: container.image?.height || container.height,
        rotation: container.rotation,
        scaleX: container.scaleX,
        scaleY: container.scaleY,
        opacity: container.opacity || 1,
        isVisible: container.visible !== false,
        isLocked: container.locked || false,
        zIndex: container.zIndex,
        image: container.image ? {
          src: container.image.src,
          width: container.image.width,
          height: container.image.height,
          originalAspectRatio: container.image.originalAspectRatio
        } : null,
        drawings: container.drawings || [],
        layerId: container.layerId,
        createdAt: container.createdAt,
        updatedAt: container.updatedAt
      };
    });

    return serializedContainers;
  }

  /**
   * סריאליזציה של הגדרות משחק
   */
  serializeGameSettings(gameContext) {
    if (!gameContext) {
      return {};
    }

    return {
      // הוסף כאן הגדרות משחק רלוונטיות
      // לדוגמה:
      // gridSize: gameContext.gridSize,
      // gridVisible: gameContext.gridVisible,
      // backgroundColor: gameContext.backgroundColor
    };
  }

  /**
   * סריאליזציה של טוקנים
   */
  async serializeTokens(tokenContext) {
    if (!tokenContext || !tokenContext.tokens) {
      return {
        tokens: [],
        selectedTokenId: null
      };
    }

    const serializedTokens = tokenContext.tokens.map(token => {
      return {
        id: token.id,
        name: token.name || `Token ${token.id.substring(6, 10)}`,
        x: token.x,
        y: token.y,
        radius: token.radius,
        fill: token.fill || '#cccccc',
        visionRadius: token.visionRadius || (token.radius * 5),
        zIndex: token.zIndex || 0,
        layerId: token.layerId || null,
        image: token.image ? {
          src: token.image.src,
          width: token.image.width,
          height: token.image.height,
          originalAspectRatio: token.image.originalAspectRatio
        } : null,
        createdAt: token.createdAt || new Date().toISOString(),
        updatedAt: token.updatedAt || new Date().toISOString()
      };
    });

    const result = {
      tokens: serializedTokens,
      selectedTokenIds: tokenContext.selectedTokenIds || []
    };

    return result;
  }

  /**
   * סריאליזציה של קבוצות טוקנים
   */
  serializeTokenGroups(tokenContext) {
    try {
      const { tokenGroups = [] } = tokenContext;
      
      return tokenGroups.map(group => ({
        id: group.id,
        name: group.name,
        tokenIds: [...group.tokenIds]
      }));

    } catch (error) {
      console.error('[GameStateManager] Error serializing token groups:', error);
      return [];
    }
  }

  /**
   * סריאליזציה של היסטוריה (מקוצרת)
   */
  serializeHistory(historyContext) {
    if (!historyContext) {
      return null;
    }

    const historyInfo = historyContext.getHistoryInfo();
    
    return {
      canUndo: historyInfo.canUndo,
      canRedo: historyInfo.canRedo,
      commandCount: historyInfo.totalCommands,
      memoryUsage: historyInfo.memoryUsage
    };
  }

  /**
   * עיבוד תמונות לטעינה
   */
  async processImagesForLoad(gameState) {
    // כרגע לא נדרש עיבוד מיוחד
    // בעתיד אפשר להוסיף cache או אופטימיזציות
    return gameState;
  }

  /**
   * יצירת thumbnail למשחק
   */
  async generateGameThumbnail(gameState, gameId = null) {
    try {
      // נחפש את התמונה הראשונה בשכבות או בקונטיינרים
      let imageUrl = null;

      // חיפוש בשכבות - תמונות נמצאות ב-layer_data
      if (gameState.layers) {
        for (const layer of gameState.layers) {
          let layerData = layer.layer_data;
          if (typeof layerData === 'string') {
            try {
              layerData = JSON.parse(layerData);
            } catch (e) {
              continue;
            }
          }
          
          if (layerData && layerData.image && layerData.image.src) {
            imageUrl = layerData.image.src;
            break;
          }
        }
      }

      // אם לא נמצא בשכבות, נחפש בקונטיינרים
      if (!imageUrl && gameState.containers) {
        for (const container of gameState.containers) {
          if (container.image && container.image.src) {
            imageUrl = container.image.src;
            break;
          }
        }
      }

      if (imageUrl && cloudinaryService.isCloudinaryUrl(imageUrl)) {
        const publicId = cloudinaryService.extractPublicId(imageUrl);
        if (publicId) {
          // שימוש בפונקציה המעודכנת עם gameId לטיפול במבנה ישן
          const thumbnailUrl = cloudinaryService.getThumbnailUrl(publicId, 300, gameId);
          
          if (thumbnailUrl) {
            return {
              url: thumbnailUrl
            };
          } else {
            console.warn('Failed to generate thumbnail URL for publicId:', publicId, 'gameId:', gameId);
          }
        } else {
          console.warn('Failed to extract public ID from URL:', imageUrl);
        }
      } else {
        console.log('No suitable Cloudinary image found for thumbnail generation');
      }

      return null;

    } catch (error) {
      console.error('Error generating thumbnail:', error);
      return null;
    }
  }

  /**
   * שמירה בבסיס הנתונים
   */
  async saveToDatabase(gameData) {
    try {
      // Import AuthInterceptor dynamically to avoid circular imports
      const { default: authInterceptor } = await import('./AuthInterceptor');
      
      const response = await authInterceptor.authenticatedFetch('/api/game-states', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(gameData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.statusText}`);
      }

      const result = await response.json();
      return {
        success: true,
        id: result.game_state_id // שימוש בשם הנכון של השדה
      };

    } catch (error) {
      console.error('Error saving to database:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * עדכון משחק בבסיס הנתונים
   */
  async updateInDatabase(gameStateId, gameData) {
    try {
      const { default: authInterceptor } = await import('./AuthInterceptor');
      
      const response = await authInterceptor.authenticatedFetch(`/api/game-states/${gameStateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(gameData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.statusText}`);
      }

      const result = await response.json();
      return {
        success: true,
        id: result.game_state_id,
        data: result
      };

    } catch (error) {
      console.error('Error updating game in database:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * קבלת משחק מבסיס הנתונים
   */
  async getGameFromDatabase(gameStateId) {
    try {
      const { default: authInterceptor } = await import('./AuthInterceptor');
      
      const response = await authInterceptor.authenticatedFetch(`/api/game-states/${gameStateId}`);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || `Server error: ${response.statusText}`);
      }

      const result = await response.json();
      
      return {
        success: true,
        data: result
      };

    } catch (error) {
      console.error('[GameStateManager] Error getting game from database:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * ניקוי מצב נוכחי
   */
  async clearCurrentState(contexts) {
    const { layerContext, containerContext, historyContext, tokenContext } = contexts;

    try {
      // ניקוי היסטוריה
      if (historyContext && historyContext.clearHistory) {
        historyContext.clearHistory();
      }

      // ניקוי שכבות וקבוצות שכבות
      if (layerContext) {
        if (layerContext.clearAllLayers) {
          layerContext.clearAllLayers();
        }
        if (layerContext.restoreLayerGroups) {
          layerContext.restoreLayerGroups([]); // Clear layer groups by setting empty array
        }
      }

      // ניקוי קונטיינרים
      if (containerContext && containerContext.clearAllContainers) {
        containerContext.clearAllContainers();
      }

      // ניקוי טוקנים
      if (tokenContext && tokenContext.setTokens) {
        tokenContext.setTokens([]);
        if (tokenContext.setSelectedTokenIds) {
          tokenContext.setSelectedTokenIds([]);
        }
        if (tokenContext.setTokenGroups) {
          tokenContext.setTokenGroups([]);
        }
      }

    } catch (error) {
      console.error('[GameStateManager] Error clearing current state:', error);
    }
  }

  /**
   * שחזור שכבות
   */
  async deserializeLayers(layersData, layerContext, activeLayerId = null) {
    if (!layerContext || !layersData || !Array.isArray(layersData)) {
      return {};
    }

    // Check if the new batch restore function is available (preferred method)
    if (layerContext.restoreMultipleLayers) {
      return layerContext.restoreMultipleLayers(layersData, activeLayerId);
    }

    // Fallback to old method if new function not available
          // Using legacy layer restoration method
    
    // Sort layers by layer_order to maintain correct order
    const sortedLayersData = [...layersData].sort((a, b) => (a.layer_order || 0) - (b.layer_order || 0));
    
    const layerIdMapping = {}; // Map old layer IDs to new layer IDs
    let newActiveLayerId = null;

    for (const layerData of sortedLayersData) {
      try {
        // יצירת שכבה חדשה עם כל הנתונים
        if (layerContext.createLayer) {
          const newLayer = await layerContext.createLayer({
            layer_name: layerData.layer_name,
            is_wall_layer: layerData.is_wall_layer || false,
            is_visible: layerData.is_visible !== false,
            layer_data: layerData.layer_data || JSON.stringify({ lines: [] }),
            layer_order: layerData.layer_order
          });
          
          if (newLayer) {
            // Store the mapping from old ID to new ID
            layerIdMapping[layerData.layer_id] = newLayer.layer_id;
            
            // Check if this was the active layer
            if (activeLayerId && layerData.layer_id === activeLayerId) {
              newActiveLayerId = newLayer.layer_id;
            }
          }
        }
      } catch (error) {
        console.error('[GameStateManager] Error deserializing layer:', {
          layerId: layerData.layer_id,
          layerName: layerData.layer_name,
          error: error.message
        });
      }
    }
    
    // Set the active layer if we found it
    if (newActiveLayerId && layerContext.setActiveLayerById) {
      layerContext.setActiveLayerById(newActiveLayerId);
    }
    
    return layerIdMapping;
  }

  /**
   * שחזור קונטיינרים
   */
  async deserializeContainers(containersData, containerContext, layerIdMapping = {}) {
    if (!containerContext || !containersData || !Array.isArray(containersData)) {
      return;
    }

    for (const containerData of containersData) {
      try {
        const newLayerId = layerIdMapping[containerData.layerId] || containerData.layerId;
        
        if (containerContext.addContainerDirect) {
          // שחזור הקונטיינר עם כל הנתונים שלו בפורמט של ContainerContext
          const restoredContainer = {
            id: containerData.id,
            name: containerData.name,
            x: containerData.x || 0,
            y: containerData.y || 0,
            rotation: containerData.rotation || 0,
            scaleX: containerData.scaleX || 1,
            scaleY: containerData.scaleY || 1,
            opacity: containerData.opacity || 1,
            visible: containerData.isVisible !== false, // Convert isVisible to visible
            locked: containerData.isLocked || false, // Convert isLocked to locked
            autoLocked: false,
            zIndex: containerData.zIndex || 0,
            image: containerData.image ? {
              id: containerData.image.id || `img-${Date.now()}`,
              src: containerData.image.src,
              width: containerData.image.width,
              height: containerData.image.height,
              originalAspectRatio: containerData.image.originalAspectRatio || (containerData.image.width / containerData.image.height)
            } : null,
            drawings: containerData.drawings || [],
            layerId: newLayerId, // Use the mapped layer ID
            createdAt: containerData.createdAt || new Date().toISOString(),
            updatedAt: containerData.updatedAt || new Date().toISOString()
          };
          
          await containerContext.addContainerDirect(restoredContainer);
        }
      } catch (error) {
        console.error('[GameStateManager] Error deserializing container:', {
          containerId: containerData.id,
          containerName: containerData.name,
          error: error.message
        });
      }
    }
  }

  /**
   * שחזור טוקנים
   */
  async deserializeTokens(tokensData, tokenContext) {
    if (!tokenContext) {
      return;
    }

    // Handle both old format (array) and new format (object with tokens and selectedTokenIds)
    let tokens = [];
    let selectedTokenIds = [];
    let tokenGroups = [];

    if (Array.isArray(tokensData)) {
      // Old format - just an array of tokens
      tokens = tokensData;
    } else if (tokensData && typeof tokensData === 'object') {
      // New format - object with tokens array and selectedTokenIds
      tokens = tokensData.tokens || [];
      selectedTokenIds = tokensData.selectedTokenIds || tokensData.selectedTokenId ? [tokensData.selectedTokenId] : [];
      tokenGroups = tokensData.tokenGroups || [];
    }

    if (!Array.isArray(tokens)) {
      return;
    }

    try {
      // If tokenContext has a setTokens method, use it to restore all tokens at once
      if (tokenContext.setTokens) {
        const restoredTokens = tokens.map(tokenData => ({
          id: tokenData.id,
          name: tokenData.name || `Token ${tokenData.id.substring(6, 10)}`,
          x: tokenData.x || 0,
          y: tokenData.y || 0,
          radius: tokenData.radius || 15,
          fill: tokenData.fill || '#cccccc',
          visionRadius: tokenData.visionRadius || (tokenData.radius * 5),
          zIndex: tokenData.zIndex || 0,
          layerId: tokenData.layerId || null,
          image: tokenData.image ? {
            src: tokenData.image.src,
            width: tokenData.image.width,
            height: tokenData.image.height,
            originalAspectRatio: tokenData.image.originalAspectRatio
          } : null,
          createdAt: tokenData.createdAt || new Date().toISOString(),
          updatedAt: tokenData.updatedAt || new Date().toISOString()
        }));
        
        tokenContext.setTokens(restoredTokens);
        
        // Restore selected tokens if available
        if (selectedTokenIds.length > 0 && tokenContext.setSelectedTokenIds) {
          tokenContext.setSelectedTokenIds(selectedTokenIds);
        }
        
        // Restore token groups if available
        if (tokenGroups.length > 0 && tokenContext.setTokenGroups) {
          tokenContext.setTokenGroups(tokenGroups);
        }
      } else {
        console.warn('[GameStateManager] tokenContext.setTokens method not available');
      }
    } catch (error) {
      console.error('[GameStateManager] Error deserializing tokens:', error);
    }
  }

  /**
   * שחזור הגדרות משחק
   */
  async deserializeGameSettings(gameSettings, gameContext) {
    if (!gameContext || !gameSettings) {
      return;
    }

    // יישום הגדרות המשחק
    // לדוגמה:
    // if (gameSettings.gridSize && gameContext.setGridSize) {
    //   gameContext.setGridSize(gameSettings.gridSize);
    // }
  }

  /**
   * שחזור היסטוריה
   */
  async deserializeHistory(historyState, historyContext) {
    if (!historyContext || !historyState) {
      return;
    }

    // כרגע רק ניקוי ההיסטוריה
    // בעתיד אפשר לשחזר חלק מההיסטוריה
    if (historyContext.clearHistory) {
      historyContext.clearHistory();
    }
  }

  /**
   * עדכון קבוצות שכבות
   */
  async deserializeLayerGroups(layerGroupsData, layerContext, layerIdMapping = {}) {
    if (!layerContext || !layerGroupsData || !Array.isArray(layerGroupsData)) {
      return;
    }

    try {
      // Map old layer IDs to new layer IDs for each group
      const restoredGroups = layerGroupsData.map(groupData => {
        const newLayerIds = groupData.layerIds
          .map(id => layerIdMapping[id] || id)
          .filter(id => id); // Remove any undefined/null IDs
        
        return {
          id: groupData.id,
          name: groupData.name,
          color: groupData.color,
          layerIds: newLayerIds,
          createdAt: groupData.createdAt || new Date().toISOString(),
          updatedAt: groupData.updatedAt || new Date().toISOString()
        };
      }).filter(group => group.layerIds.length > 0); // Only keep groups that have valid layers

      // Use restoreLayerGroups to restore all groups at once
      if (layerContext.restoreLayerGroups) {
        layerContext.restoreLayerGroups(restoredGroups);
      } else {
        console.warn('[GameStateManager] restoreLayerGroups method not available in layerContext');
      }
      
    } catch (error) {
      console.error('[GameStateManager] Error deserializing layer groups:', error);
    }
  }

  /**
   * בדיקת תאימות גרסה
   */
  isVersionCompatible(version) {
    if (!version) return true; // גרסה ישנה ללא מספר גרסה
    
    const [major, minor] = version.split('.').map(Number);
    const [currentMajor, currentMinor] = this.version.split('.').map(Number);
    
    // תאימות לאחור בתוך אותה גרסה major
    return major === currentMajor && minor <= currentMinor;
  }

  /**
   * הערכת גודל מצב המשחק
   */
  estimateSize(gameState) {
    try {
      return JSON.stringify(gameState).length;
    } catch (error) {
      return 0;
    }
  }

  /**
   * ייצוא מקומי של מצב המשחק
   */
  exportGameStateLocally(gameState, filename = 'game-state.json') {
    try {
      const dataStr = JSON.stringify(gameState, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      return { success: true };
    } catch (error) {
      console.error('Error exporting game state:', error);
      return { success: false, error: error.message };
    }
  }

  /**
   * ייבוא מקומי של מצב משחק
   */
  async importGameStateLocally(file) {
    try {
      const text = await file.text();
      const gameState = JSON.parse(text);
      
      // בדיקת תקינות בסיסית
      if (!gameState.metadata || !gameState.metadata.version) {
        throw new Error('Invalid game state file format');
      }
      
      return {
        success: true,
        gameState
      };
    } catch (error) {
      console.error('Error importing game state:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * קבלת רשימת תמונות של משחק
   * @param {string} gameId - מזהה המשחק
   * @param {string} type - סוג התמונות (אופציונלי)
   * @returns {Promise<Object>} רשימת התמונות
   */
  async getGameImages(gameId, type = null) {
    try {
      const result = await cloudinaryService.getGameImages(gameId, type);
      return result;
    } catch (error) {
      console.error('Error getting game images:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  /**
   * מחיקת תמונות משחק מ-Cloudinary
   * @param {Object} gameMetadata - metadata של המשחק
   * @param {string} gameId - מזהה המשחק
   * @returns {Promise<boolean>} האם המחיקה הצליחה
   */
  async deleteGameImages(gameMetadata, gameId) {
    try {
      const metadata = typeof gameMetadata === 'string' 
        ? JSON.parse(gameMetadata) 
        : gameMetadata;

      const imagesToDelete = [];

      // איסוף כל ה-cloudinaryIds מה-metadata
      if (metadata.cloudinaryId) {
        imagesToDelete.push({
          publicId: metadata.cloudinaryId,
          resourceType: 'raw'
        });
      }

      // אם יש מידע על תמונות נוספות ב-metadata
      if (metadata.imageIds && Array.isArray(metadata.imageIds)) {
        metadata.imageIds.forEach(imageId => {
          imagesToDelete.push({
            publicId: imageId,
            resourceType: 'image'
          });
        });
      }

      // מחיקת כל התמונות
      const deletePromises = imagesToDelete.map(async (item) => {
        try {
          return await cloudinaryService.deleteFile(item.publicId, item.resourceType);
        } catch (error) {
          console.warn(`Failed to delete ${item.publicId}:`, error);
          return { success: false, error: error.message };
        }
      });

      // const results = await Promise.all(deletePromises); // Commented: unused variable
      // const successCount = results.filter(r => r.success).length; // Commented: unused variable
      await Promise.all(deletePromises); // Execute deletion promises without storing results
      
      return true;

    } catch (error) {
      console.error('Error deleting game images:', error);
      return false;
    }
  }
}

export default GameStateManager; 